import TableOfContent from "@bmi-digital/components/table-of-content";
import { replaceSpaces } from "@bmi-digital/components/utils";
import { graphql } from "gatsby";
import React, { createContext, useMemo } from "react";
import CardCollectionSection from "./CardCollectionSection";
import DocumentDownloadSection from "./DocumentDownloadSection";
import EmbeddedRegistrationFormSection from "./embedded-registration-form-section";
import EmbeddedScriptSection from "./EmbeddedScriptSection";
import ExploreBarSection from "./ExploreBarSection";
import FormSection from "./FormSection";
import IframeSection from "./IframeSection";
import MediaGallerySection from "./MediaGallerySection";
import PromoSection from "./PromoSection";
import SignupBlock from "./SignupBlock";
import SyndicateSection from "./SyndicateSection";
import SystemConfiguratorSection from "./SystemConfiguratorSection";
import TabsOrAccordionSection from "./TabsOrAccordionSection";
import TeamSection from "./TeamSection";
import TitleWithContentSection from "./TitleWithContentSection";
import VideoSection from "./VideoSection";
import CarouselSection from "./carousel-section/CarouselSection";
import ServiceLocatorSection from "./service-locator-section/index";
import LeadBlockSection from "./LeadBlockSection";
import type { LeadBlockSectionData } from "./LeadBlockSection";
import type { SignupBlockSectionData } from "./SignupBlock";
import type { SyndicateSectionData } from "./SyndicateSection";
import type { SystemConfiguratorSectionData } from "./SystemConfiguratorSection";
import type { TabsOrAccordionSectionData } from "./TabsOrAccordionSection";
import type { TeamSectionData } from "./TeamSection";
import type { TitleWithContentSectionData } from "./TitleWithContentSection";
import type { VideoSectionData } from "./VideoSection";
import type { CarouselSectionData } from "./carousel-section/CarouselSection";
import type { ServiceLocatorSectionData } from "./service-locator-section/index";
import type { PromoSectionData } from "./PromoSection";
import type { MediaGallerySectionData } from "./MediaGallerySection";
import type { IframeSectionData } from "./IframeSection";
import type { FormSectionData } from "./FormSection";
import type { ExploreBarSectionData } from "./ExploreBarSection";
import type { EmbeddedRegistrationFormSectionData } from "./embedded-registration-form-section";
import type { EmbeddedScriptSectionData } from "./EmbeddedScriptSection";
import type { DocumentDownloadSectionData as DocumentDownloadSectionData } from "./DocumentDownloadSection";
import type { CardCollectionSectionData } from "./CardCollectionSection";

export type SectionData =
  | CardCollectionSectionData
  | CarouselSectionData
  | DocumentDownloadSectionData
  | EmbeddedRegistrationFormSectionData
  | EmbeddedScriptSectionData
  | ExploreBarSectionData
  | FormSectionData
  | IframeSectionData
  | LeadBlockSectionData
  | MediaGallerySectionData
  | PromoSectionData
  | ServiceLocatorSectionData
  | SignupBlockSectionData
  | SyndicateSectionData
  | SystemConfiguratorSectionData
  | TabsOrAccordionSectionData
  | TeamSectionData
  | TitleWithContentSectionData
  | VideoSectionData;

export type SectionsProps = {
  data: readonly SectionData[];
  startIndex?: number;
  pageTypename?: string;
};

export const sectionsMap = {
  "card-collection-section": CardCollectionSection,
  "carousel-section": CarouselSection,
  "document-download-section": DocumentDownloadSection,
  "embedded-registration-form-section": EmbeddedRegistrationFormSection,
  "embedded-script-section": EmbeddedScriptSection,
  "explore-bar-section": ExploreBarSection,
  "form-section": FormSection,
  iframe: IframeSection,
  "lead-block-section": LeadBlockSection,
  "media-gallery-section": MediaGallerySection,
  "promo-section": PromoSection,
  "service-locator-section": ServiceLocatorSection,
  "signup-block": SignupBlock,
  "syndicate-section": SyndicateSection,
  "system-configurator-section": SystemConfiguratorSection,
  "tabs-or-accordion-section": TabsOrAccordionSection,
  "team-section": TeamSection,
  "title-with-content": TitleWithContentSection,
  "video-section": VideoSection
};

type DisplayProps = {
  backgroundColor: PromoSectionData["backgroundColor"];
  isReversed?: boolean;
};

type Context = {
  [id: string]: DisplayProps;
};

export const SectionsContext = createContext<Context>({});

// TODO: This should be exported by the card collection.
type ThemeOptions = "cardCollectionRowType";

type Theme = Partial<Record<ThemeOptions, unknown>>;

const pageTypenameToThemeMap: Record<string, Theme> = {
  "home-page": {
    cardCollectionRowType: "single-row"
  }
};

const createThemeMap = (data: SectionsProps["data"]): Context => {
  return data.reduce<Context>((carry, section, index) => {
    const previousSection = data[index - 1];
    if (section.blockType !== "promo-section") {
      return carry;
    }

    const { id, backgroundColor } = section;

    if (previousSection?.blockType !== "promo-section") {
      return {
        ...carry,
        [id]: {
          isReversed: true,
          backgroundColor: backgroundColor || "White"
        }
      };
    }

    const previousCarry = carry[previousSection.id];

    return {
      ...carry,
      [id]: {
        // eslint-disable-next-line security/detect-object-injection
        isReversed: previousCarry ? !previousCarry.isReversed : true,
        backgroundColor:
          backgroundColor ||
          // eslint-disable-next-line security/detect-object-injection
          (previousCarry?.backgroundColor === "White" ? "Alabaster" : "White")
      }
    };
  }, {});
};

const Sections = (props: SectionsProps) => {
  const { data, startIndex, pageTypename } = props;
  const themeMap = useMemo(() => createThemeMap(data), [data]);

  return (
    <SectionsContext.Provider value={themeMap}>
      {data.map((section, index) => {
        const Component: React.ElementType = sectionsMap[section.blockType];
        const title =
          section.blockType === "embedded-registration-form-section"
            ? section.embeddedRegistrationFormSectionTitle
            : section.title;

        const sectionId = `section-${index}`;
        const sectionComponent = (
          <Component
            id={sectionId}
            data={section}
            position={startIndex ?? index}
            theme={
              // eslint-disable-next-line security/detect-object-injection
              (pageTypename && pageTypenameToThemeMap[pageTypename]) || {}
            }
            key={sectionId}
            data-testid={`section-${title ? replaceSpaces(title) : index}`}
          />
        );

        return title ? (
          <TableOfContent.Anchor title={title} key={`section-${index}`}>
            {sectionComponent}
          </TableOfContent.Anchor>
        ) : (
          sectionComponent
        );
      })}
    </SectionsContext.Provider>
  );
};

export default Sections;

export const query = graphql`
  fragment SectionsFragment on PayloadSection {
    ...CardCollectionSectionFragment
    ...CarouselSectionFragment
    ...DocumentDownloadSectionFragment
    ...EmbeddedRegistrationFormSectionFragment
    ...EmbeddedScriptSectionFragment
    ...ExploreBarSectionFragment
    ...FormSectionFragment
    ...IframeSectionFragment
    ...MediaGallerySectionFragment
    ...PromoSectionFragment
    ...ServiceLocatorSectionFragment
    ...SignupBlockSectionFragment
    ...SyndicateSectionFragment
    ...SystemConfiguratorSectionFragment
    ...TabsOrAccordionSectionFragment
    ...TeamSectionFragment
    ...TitleWithContentSectionFragment
    ...VideoSectionFragment
  }
`;
